import { IWebResource } from "../../interfaces/IWebResource";
import { getWebResourceUrl, sendMetadataGetRequestAsync } from "@definitions/MetadataApi";
import { IPromiseCache, cachedWrapper } from "@utilities/MemoryCachingHelpers";
import { talxis_WebResource } from "@src/app/interfaces/talxis_WebResource";

export class ScriptLoader {
    private static _script: IPromiseCache<boolean> = {};
    private static _libraryWithDependencies: IPromiseCache<IWebResource> = {};
    private static _libraryDependencies: IPromiseCache<talxis_RetrieveWebResourceDefinitionResponse> = {};

    static loadAsync(name: string, url: string, targetDocument?: HTMLIFrameElement): Promise<boolean> {
        let cache = this._script;
        if (targetDocument) {
            if (!targetDocument.contentWindow._cachedScripts) {
                targetDocument.contentWindow._cachedScripts = {};
            }
            cache = targetDocument.contentWindow._cachedScripts;
        }

        return cachedWrapper(name, () => new Promise((resolve, reject) => {
            const script: HTMLScriptElement = document.createElement("script");
            script.async = true;

            if (!url.startsWith('/') && !url.startsWith('http')) {
                console.warn(`DEPRECATED: Loading script ${name} as in-line script. This will be removed in future versions.`);
                //TODO: Scripts should be loaded from metadata service.
                // script.src = `data:text/javascript;base64,${url}`
                script.innerHTML = this._b64DecodeUnicode(url);
                resolve(true);
            }
            else {
                script.src = url;
            }

            script.onload = () => {
                resolve(true);
            };
            script.onerror = () => {
                reject(new Error(`Unable to load script ${name} from ${url}!`));
            };

            if (targetDocument) {
                targetDocument.contentWindow.document.head?.appendChild(script);
            }
            else {
                document.body.appendChild(script);
            }

            console.log(`Loading remote webresource ${name} to the DOM.`, targetDocument);
        }), cache);
    }

    private static async _getDefinition(libraryName: string): Promise<talxis_RetrieveWebResourceDefinitionResponse> {
        return cachedWrapper(libraryName, () => new Promise(async (resolve, reject) => {
            try {
                const webResourceDefinitions = await sendMetadataGetRequestAsync(`v1.0/talxis_RetrieveWebResourceDefinition(Name='${encodeURIComponent(libraryName)}')`);
                resolve(webResourceDefinitions.json());
            } catch (error) {
                reject(`Web Resource Library ${libraryName} could not be loaded! Check if it is available in the system.`);
            }
        }), this._libraryDependencies);
    }

    static async loadWebResourceLibraryAndDependencies(libraryName: string, targetDocument?: HTMLIFrameElement): Promise<IWebResource> {
        const webResource: IWebResource = {
            translations: {}
        };
        const name = targetDocument ? `${targetDocument.getAttribute('id')}: ${libraryName}` : libraryName;

        let cache = this._libraryWithDependencies;
        if (targetDocument) {
            if (!targetDocument.contentWindow._cachedWebResources) {
                targetDocument.contentWindow._cachedWebResources = {};
            }
            cache = targetDocument.contentWindow._cachedWebResources;
        }

        return cachedWrapper(name, () => new Promise(async (resolve, reject) => {
            try {
                const definition = await ScriptLoader._getDefinition(libraryName);
                for (const dependency of definition.dependencies) {
                    if (dependency.name.endsWith(".resx")) {
                        const resxNameWithLanguageId = dependency.name.slice(0, dependency.name.indexOf(".resx"));

                        if (resxNameWithLanguageId.endsWith(`${window.Xrm.Utility.getGlobalContext().userSettings.languageId}`)) {
                            const resxName = dependency.name.substring(0, resxNameWithLanguageId.lastIndexOf("."));

                            webResource.translations[resxName] = JSON.parse(dependency.content || '{}');
                        }
                    } else {
                        const nestedWebResource = await this.loadWebResourceLibraryAndDependencies(dependency.name, targetDocument);
                        webResource.translations = { ...webResource.translations, ...nestedWebResource.translations };
                    }
                }

                window.TALXIS.Portal.WebResouces = { ...window.TALXIS.Portal.WebResouces, ...webResource.translations };
                await this.loadAsync(libraryName, getWebResourceUrl(`webresources/${libraryName}`), targetDocument);
                resolve(webResource);
            } catch (error) {
                reject(error);
            }
        }), cache);
    }

    public static _b64DecodeUnicode(str: string): string {
        return decodeURIComponent(atob(str).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
    }
}

interface talxis_RetrieveWebResourceDefinitionResponse {
    id: string;
    dependencies: talxis_WebResource[];
}